import {
  SyntheticSearchFilterTypes,
  SyntheticSearchFilterValueTypes,
  withSyntheticFilterTypes,
} from 'new/utils/syntheticSearchFilters';

export const RESOURCE_SUGGESTIONS_COUNT = 3;

export const SearchFilterViews = {
  PIPELINES: 'PIPELINES',
  PROJECT_PIPELINES: 'PROJECT_PIPELINES',
  SQL_AUTOMATIONS: 'SQL_AUTOMATIONS',
  MCP_FLOWS: 'MCP_FLOWS',
  PROJECTS: 'PROJECTS',
  SEARCH_PIPELINES: 'SEARCH_PIPELINES',
  SOURCES: 'SOURCES',
  MY_DATA_SETS: 'MY_DATA_SETS',
  AVAILABLE_DATA_SETS: 'AVAILABLE_DATA_SETS',
  DATA_SINKS: 'DATA_SINKS',
  DATA_MAPS: 'DATA_MAPS',
  CREDENTIALS: 'CREDENTIALS',
  REUSABLE_TRANSFORMS: 'REUSABLE_TRANSFORMS',
  SCHEMA_SPECS: 'SCHEMA_SPECS',
  API_KEYS: 'API_KEYS',
  SERVICE_KEYS: 'SERVICE_KEYS',
  AI_COMPATIBLE_NEXSETS: 'AI_COMPATIBLE_NEXSETS',
} as { [key: string]: SearchFilterViewsType };

export type SearchFilterViewsType =
  | 'PIPELINES'
  | 'PROJECT_PIPELINES'
  | 'SQL_AUTOMATIONS'
  | 'MCP_FLOWS'
  | 'PROJECTS'
  | 'SEARCH_PIPELINES'
  | 'SOURCES'
  | 'MY_DATA_SETS'
  | 'AVAILABLE_DATA_SETS'
  | 'DATA_SINKS'
  | 'DATA_MAPS'
  | 'CREDENTIALS'
  | 'REUSABLE_TRANSFORMS'
  | 'SCHEMA_SPECS'
  | 'API_KEYS'
  | 'SERVICE_KEYS'
  | 'AI_COMPATIBLE_NEXSETS';

export const SearchFilterOpTypes = {
  EQUALS: 'eq',
  NOT_EQUALS: 'not_eq',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  STARTS_WITH: 'starts_with',
  ENDS_WITH: 'ends_with',
  GREATER_THAN: 'gt',
  LESS_THAN: 'lt',
  IS_EMPTY: 'empty',
  IS_NOT_EMPTY: 'not_empty',
};

export const SearchFilterOps = {
  [SearchFilterOpTypes.EQUALS]: {
    type: SearchFilterOpTypes.EQUALS,
    label: 'is',
    operators: ['is'],
  },
  [SearchFilterOpTypes.NOT_EQUALS]: {
    type: SearchFilterOpTypes.NOT_EQUALS,
    label: 'is not',
    operators: ['!is', 'isNot'],
  },
  [SearchFilterOpTypes.CONTAINS]: {
    type: SearchFilterOpTypes.CONTAINS,
    label: 'contains',
    operators: ['contains'],
    needsCustomVal: true,
  },
  [SearchFilterOpTypes.NOT_CONTAINS]: {
    type: SearchFilterOpTypes.NOT_CONTAINS,
    label: 'does not contain',
    operators: ['!contains', 'notContains'],
    needsCustomVal: true,
  },
  [SearchFilterOpTypes.STARTS_WITH]: {
    type: SearchFilterOpTypes.STARTS_WITH,
    label: 'starts with',
    operators: ['startsWith'],
    needsCustomVal: true,
  },
  [SearchFilterOpTypes.ENDS_WITH]: {
    type: SearchFilterOpTypes.ENDS_WITH,
    label: 'ends with',
    operators: ['endsWith'],
    needsCustomVal: true,
  },
  [SearchFilterOpTypes.GREATER_THAN]: {
    type: SearchFilterOpTypes.GREATER_THAN,
    label: 'is greater than',
    operators: ['gt', 'greaterThan'],
    needsCustomVal: true,
  },
  [SearchFilterOpTypes.LESS_THAN]: {
    type: SearchFilterOpTypes.LESS_THAN,
    label: 'is less than',
    operators: ['lt', 'lessThan'],
    needsCustomVal: true,
  },
  [SearchFilterOpTypes.IS_EMPTY]: {
    type: SearchFilterOpTypes.IS_EMPTY,
    label: 'is empty',
    operators: ['isEmpty', 'empty'],
    needsVal: false,
  },
  [SearchFilterOpTypes.IS_NOT_EMPTY]: {
    type: SearchFilterOpTypes.IS_NOT_EMPTY,
    label: 'is not empty',
    operators: ['notEmpty', '!empty', '!isEmpty'],
    needsVal: false,
  },
};

export const SearchFilterTypes = {
  TAG: 'tags',
  ID: 'id',
  NAME: 'name',
  DESCRIPTION: 'description',
  STATUS: 'status',
  FLOW_TYPE: 'flow_type',
  PROJECT_ID: 'project_id',
  SOURCE_ID: 'source_id', // for flows search
  CONNECTOR_CODE: 'connector_code',
  NEXSET_ID: 'nexset_id',
  PARENT_DATA_SET_ID: 'parent_data_set_id',
  DESTINATION_ID: 'destination_id',
  ATTR_NAME: 'attr_name',
  API_KEY: 'api_key',
  NEXSET_API_COMPATIBLE: 'nexset_api_compatible',
  RUNTIME_STATUS: 'runtime_status',
  DISCOVERABLE_DATA: 'discoverable_data',
  // UI-only keys translated before the request (IS_VALID, ARCHIVED, ...)
  ...SyntheticSearchFilterTypes,
};

// How the search bar parses a filter's input, not the API data type. Only 'string', 'int'
// and 'enum' are handled; true/false filters use 'enum' (see syntheticSearchFilters).
export const SearchFilterValueTypes = {
  [SearchFilterTypes.TAG]: 'enum',
  [SearchFilterTypes.ID]: 'int',
  [SearchFilterTypes.NAME]: 'string',
  [SearchFilterTypes.DESCRIPTION]: 'string',
  [SearchFilterTypes.STATUS]: 'enum',
  [SearchFilterTypes.FLOW_TYPE]: 'enum',
  [SearchFilterTypes.PROJECT_ID]: 'int',
  [SearchFilterTypes.SOURCE_ID]: 'int',
  [SearchFilterTypes.CONNECTOR_CODE]: 'enum',
  [SearchFilterTypes.NEXSET_ID]: 'int',
  [SearchFilterTypes.PARENT_DATA_SET_ID]: 'int',
  [SearchFilterTypes.DESTINATION_ID]: 'int',
  [SearchFilterTypes.ATTR_NAME]: 'string',
  [SearchFilterTypes.API_KEY]: 'string',
  [SearchFilterTypes.RUNTIME_STATUS]: 'enum',
  [SearchFilterTypes.DISCOVERABLE_DATA]: 'bool',
  ...SyntheticSearchFilterValueTypes,
};

export const SearchFilterTypeAliases = {
  [SearchFilterTypes.SOURCE_ID]: 'data_source_id',
  [SearchFilterTypes.NEXSET_ID]: 'data_set_id',
  [SearchFilterTypes.DESTINATION_ID]: 'data_sink_id',
};

export const InterSearchFilterOps = {
  AND: 'AND',
  OR: 'OR',
};

export const ACLQueryModeTypes = {
  MODE_OWNER: {
    key: 'MODE_OWNER',
    label: 'Owned by Me',
    desc: '',
    value: 'MODE_OWNER',
    queryParam: 'default',
    searchValue: 'owner',
    listOrder: 1,
  },
  MODE_COLLABORATOR: {
    key: 'MODE_COLLABORATOR',
    label: 'Accessible to Me',
    desc: 'All resources I have full permissions or read only permissions for',
    value: 'MODE_COLLABORATOR',
    queryParam: 'collaborator',
    searchValue: 'collaborator',
    listOrder: 2,
  },
};

// Synthetic filters (new/utils/syntheticSearchFilters) are appended to the views they declare
export const AvailableFilterTypesByView = withSyntheticFilterTypes({
  [SearchFilterViews.PIPELINES]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.TAG,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.NEXSET_ID,
    SearchFilterTypes.DESTINATION_ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
    SearchFilterTypes.FLOW_TYPE,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.PROJECT_PIPELINES]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.TAG,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.NEXSET_ID,
    SearchFilterTypes.DESTINATION_ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
    SearchFilterTypes.FLOW_TYPE,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.SEARCH_PIPELINES]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.TAG,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.NEXSET_ID,
    SearchFilterTypes.DESTINATION_ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
    SearchFilterTypes.FLOW_TYPE,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.SQL_AUTOMATIONS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.TAG,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
  ],
  [SearchFilterViews.MCP_FLOWS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.TAG,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.NEXSET_ID,
    SearchFilterTypes.DESTINATION_ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
    SearchFilterTypes.FLOW_TYPE,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.PROJECTS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
  ],
  [SearchFilterViews.MY_DATA_SETS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
    SearchFilterTypes.CONNECTOR_CODE,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.PARENT_DATA_SET_ID,
    SearchFilterTypes.ATTR_NAME,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
  ],
  [SearchFilterViews.AVAILABLE_DATA_SETS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
    SearchFilterTypes.ATTR_NAME,
  ],
  [SearchFilterViews.SERVICE_KEYS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
  ],
  [SearchFilterViews.REUSABLE_TRANSFORMS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
  ],
  [SearchFilterViews.DATA_SINKS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.SOURCES]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
    SearchFilterTypes.STATUS,
    SearchFilterTypes.RUNTIME_STATUS,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.CREDENTIALS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.CONNECTOR_CODE,
  ],
  [SearchFilterViews.AI_COMPATIBLE_NEXSETS]: [
    SearchFilterTypes.ID,
    SearchFilterTypes.NAME,
    SearchFilterTypes.DESCRIPTION,
    SearchFilterTypes.TAG,
    SearchFilterTypes.CONNECTOR_CODE,
    SearchFilterTypes.SOURCE_ID,
    SearchFilterTypes.PARENT_DATA_SET_ID,
    SearchFilterTypes.ATTR_NAME,
    SearchFilterTypes.STATUS,
  ],
});

export const DefaultFiltersByView = {
  [SearchFilterViews.PIPELINES]: [],
  [SearchFilterViews.PROJECT_PIPELINES]: [],
  [SearchFilterViews.SQL_AUTOMATIONS]: [],
  [SearchFilterViews.MCP_FLOWS]: [],
  [SearchFilterViews.PROJECTS]: [],
  [SearchFilterViews.SEARCH_PIPELINES]: [],
  [SearchFilterViews.SOURCES]: [],
  [SearchFilterViews.MY_DATA_SETS]: [],
  [SearchFilterViews.AVAILABLE_DATA_SETS]: [],
  [SearchFilterViews.DATA_SINKS]: [],
  [SearchFilterViews.DATA_MAPS]: [],
  [SearchFilterViews.CREDENTIALS]: [],
  [SearchFilterViews.REUSABLE_TRANSFORMS]: [],
  [SearchFilterViews.SCHEMA_SPECS]: [],
  [SearchFilterViews.API_KEYS]: [],
  [SearchFilterViews.SERVICE_KEYS]: [],
  [SearchFilterViews.AI_COMPATIBLE_NEXSETS]: [],
};
